import React, { useState, useEffect } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import CustomTable from "@/components/common/table/CustomTable";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import dateHandler from "@/composables/dateUtil";
import { CmsPermissions } from "@/constants/permission-modules/base-permissions";
import { usePermissionState } from "@/providers/PermissionProvider";
interface UserType {
  id: number;
  user_type: string;
}

const CMSPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [categories, setCategories] = useState<UserType[]>([]);
  const [selectedUserType, setSelectedUserType] = useState<number | null>(null);
  const { formatDate } = dateHandler();
  const { isPermitted } = usePermissionState();

  const fetchUserTypes = async () => {
    try {
      const { authenticatedCall: getUserTypes } =
        actionsHandler("userTypeList");
      const response = await dispatch(
        getUserTypes({
          method: "get",
        }),
      );

      if (response.ok && response.data) {
        setCategories(
          response.data.map((item: any) => ({
            id: item.id,
            user_type: item.user_type,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };

  useEffect(() => {
    fetchUserTypes();
  }, []);

  const fetchData = async (params: {
    page: number;
    limit: number;
    search?: string;
    sortColumn?: string;
    sortDirection?: "asc" | "desc";
  }) => {
    try {
      const { authenticatedCall: getCMSContent } = actionsHandler("cmsContent");

      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        is_archive: isArchiveView ? "true" : "false",
        limit: params.limit.toString(),
        ...(params.search && { search: params.search }),
        ...(params.sortColumn && {
          ordering: `${params.sortDirection === "desc" ? "-" : ""}${params.sortColumn}`,
        }),
        ...(selectedUserType !== null && {
          user_type: selectedUserType.toString(),
        }),
      }).toString();
      const response = await dispatch(
        getCMSContent({
          method: "get",
          customQuery: queryParams,
        }),
      );

      if (response.ok) {
        return response;
      } else {
        toast.error(response.data?.message || "Failed to fetch CMS content");
      }
    } catch (error) {
      console.error("Error fetching CMS content:", error);
    }
  };

  const handleArchived = async (row: any, isCurrentlyArchived: boolean) => {
    try {
      const { authenticatedCall: archiveCMS } =
        actionsHandler("cmsContentArchive");

      const payload = {
        is_archive: !isCurrentlyArchived,
      };

      const response = await dispatch(
        archiveCMS({
          payload,
          method: "patch",
          customParams: [row.id + "/"],
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            `CMS Page ${payload.is_archive ? "archived" : "unarchived"} successfully`,
        );
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Archive error:", error);
    }
  };

  useEffect(() => {
    setForceReset(true);
  }, [isArchiveView]);
  useEffect(() => {
    setForceReset(true);
  }, [selectedUserType]);

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a
                href="/"
                className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
              >
                <img
                  src={BackIcon}
                  alt="Back Icon"
                  className="tw-cursor-pointer"
                />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Shield CMS Page
              </h1>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-mt-[147px]">
        <div className="tw-w-full">
          <div className="tw-border tw-border-t-0 tw-border-gray-100">
            <CustomTable
              columns={
                isArchiveView
                  ? [
                      {
                        header: "CMS Page Name",
                        accessor: "title",
                        sortable: true,
                        sortKey: "title",
                      },
                      {
                        header: "User Type",
                        accessor: (row: any) => row.user_type.user_type,
                        sortable: true,
                        sortKey: "user_type",
                      },
                      {
                        header: "Created on",
                        accessor: (row: any) => formatDate(row.created_at),
                        sortable: true,
                        sortKey: "created_at",
                      },
                      {
                        header: "Archived on",
                        accessor: (row: any) => formatDate(row.updated_at),
                        sortable: true,
                        sortKey: "updated_at",
                      },
                      {
                        header: "Unarchive",
                        accessor: (row: any) => (
                          <button
                            className="tw-text-blue-500"
                            onClick={() => handleArchived(row, isArchiveView)}
                            title="Unarchive"
                          >
                            <img
                              src={UnarchiveIcon}
                              alt="Unarchive Icon"
                              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                            />
                          </button>
                        ),
                      },
                    ]
                  : [
                      {
                        header: "CMS Page Name",
                        accessor: "title",
                        sortable: true,
                        sortKey: "title",
                      },
                      {
                        header: "User Type",
                        accessor: (row: any) => row.user_type.user_type,
                        sortable: true,
                        sortKey: "user_type",
                      },
                      {
                        header: "Created on",
                        accessor: (row: any) => formatDate(row.created_at),
                        sortable: true,
                        sortKey: "created_at",
                      },
                      ...(isPermitted(CmsPermissions.UPDATE_CMS) ||
                      isPermitted(CmsPermissions.ARCHIVE_CMS)
                        ? [
                            {
                              header: "Actions",
                              accessor: (row: any) => (
                                <div className="tw-flex tw-items-center">
                                  {isPermitted(CmsPermissions.UPDATE_CMS) && (
                                    <button
                                      onClick={() =>
                                        navigate(`/edit-cms-page/${row.id}`)
                                      }
                                      title="Edit"
                                    >
                                      <img
                                        src={EditIcon}
                                        alt="Edit Icon"
                                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                                      />
                                    </button>
                                  )}
                                  {isPermitted(CmsPermissions.ARCHIVE_CMS) && (
                                    <button
                                      className="tw-text-blue-500"
                                      onClick={() =>
                                        handleArchived(row, isArchiveView)
                                      }
                                      title="Archive"
                                    >
                                      <img
                                        src={DownloadIcon}
                                        alt="Archive Icon"
                                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                                      />
                                    </button>
                                  )}
                                </div>
                              ),
                            },
                          ]
                        : []),
                    ]
              }
              fetchData={fetchData}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              filterOptions={{
                show: true,
                options: categories.map((cat) => ({
                  id: cat.id,
                  name: cat.user_type,
                })),
                placeholder: "Select User Type",
                onFilterChange: (selected: { id: number; name: string }[]) => {
                  if (selected.length > 0) {
                    setSelectedUserType(selected[0].id);
                  } else {
                    setSelectedUserType(null);
                  }
                },
              }}
              addButton={
                isPermitted(CmsPermissions.CREATE_CMS)
                  ? {
                      text: "Add",
                      icon: PlusIcon,
                      onClick: () => navigate("/add-cms-page"),
                    }
                  : undefined
              }
              archiveButton={{
                text: isArchiveView ? "Back to List" : "Archived",
                icon: isArchiveView ? ListIcon : DownloadIcon,
                onClick: () => setIsArchiveView(!isArchiveView),
                isArchiveView,
              }}
              forceReset={forceReset}
              setForceReset={setForceReset}
              noDataMessage={`No CMS data found`}
              noSearchDataMessage={`No matching CMS data found`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSPage;
